/**
 * Camera permission utilities.
 *
 * Provides helpers to check, request and handle camera permissions in a
 * browser-consistent way.  All functions are purely async and do NOT throw —
 * they return a typed result so callers can handle each case explicitly.
 */

export type PermissionStatus =
  | "granted"    // camera already accessible
  | "prompt"     // permission dialog will appear on request
  | "denied"     // user previously denied; cannot request again
  | "unsupported" // Permissions API or getUserMedia not available
  | "unknown";   // couldn't determine status

export interface CameraPermissionResult {
  status: PermissionStatus;
  /** Human-readable reason (populated on denied / unsupported). */
  reason?: string;
}

/**
 * Query the current camera permission state without prompting the user.
 *
 * Uses the Permissions API when available, falls back to "unknown" on older
 * browsers (e.g. Safari < 16).
 */
export async function queryCameraPermission(): Promise<CameraPermissionResult> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices) {
    return { status: "unsupported", reason: "getUserMedia is not supported in this browser." };
  }

  if (!navigator.permissions?.query) {
    return { status: "unknown" };
  }

  try {
    const result = await navigator.permissions.query({ name: "camera" as PermissionName });
    return { status: result.state as PermissionStatus };
  } catch {
    return { status: "unknown" };
  }
}

/**
 * Request camera access and return the resulting MediaStream.
 *
 * Maps browser-specific errors onto actionable `CameraPermissionResult`
 * values so callers can show the right UI instead of catching raw DOMExceptions.
 *
 * @param constraints - Optional MediaStreamConstraints override.
 */
export async function requestCameraAccess(
  constraints?: MediaStreamConstraints
): Promise<{ stream: MediaStream; permission: CameraPermissionResult }> {
  const defaults: MediaStreamConstraints = {
    video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
    audio: false,
  };

  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints ?? defaults);
    return { stream, permission: { status: "granted" } };
  } catch (err: any) {
    const name: string = err?.name ?? "";

    if (name === "NotAllowedError" || name === "PermissionDeniedError") {
      return {
        stream: null as any,
        permission: {
          status: "denied",
          reason: "Camera access was denied. Please allow camera permissions in your browser settings and try again.",
        },
      };
    }

    if (name === "NotFoundError" || name === "DevicesNotFoundError") {
      return {
        stream: null as any,
        permission: {
          status: "unsupported",
          reason: "No camera was found. Please connect a camera and try again.",
        },
      };
    }

    if (name === "NotReadableError" || name === "TrackStartError") {
      return {
        stream: null as any,
        permission: {
          status: "unknown",
          reason: "Camera is already in use by another application. Please close it and try again.",
        },
      };
    }

    if (name === "OverconstrainedError") {
      return {
        stream: null as any,
        permission: {
          status: "unknown",
          reason: "Camera constraints not supported. Trying with different settings...",
        },
      };
    }

    return {
      stream: null as any,
      permission: {
        status: "unknown",
        reason: err?.message ?? "Unable to access the camera.",
      },
    };
  }
}

/**
 * Whether a failed camera request is worth retrying. "denied" (user
 * explicitly refused) and "unsupported" (no camera device at all) can never
 * be fixed by retrying — surfacing the error immediately instead of burning
 * several seconds of backoff first. Everything else (busy camera,
 * unsupported constraints, anything unrecognized) is treated as
 * potentially transient and retried — failing open avoids turning a
 * recoverable hiccup into a hard failure on browsers that throw
 * non-standard error names (notably Safari/iOS).
 */
export function isRetryableCameraError(status: PermissionStatus): boolean {
  return status !== "denied" && status !== "unsupported";
}

/**
 * Stop all tracks on a MediaStream and release the camera.
 * Safe to call with null/undefined.
 */
export function releaseCameraStream(stream: MediaStream | null | undefined): void {
  if (!stream) return;
  stream.getTracks().forEach((t) => t.stop());
}

/**
 * Returns true if the browser supports the camera APIs needed by the SDK.
 */
export function isCameraSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined"
  );
}
